import * as Dfns from "date-fns/fp";
import moment from "moment-timezone";
import * as R from "ramda";
import {
  BpmDateString,
  CompanyFiscalCal,
  FiscalCalType,
  FiscalPatternCal,
  getMonthLabelPattern,
  getQuarterLabelPattern,
  getQuarterLabelYearly,
  getYearLabelPattern,
  getYearLabelYearly,
} from "@blisspointmedia/bpm-types/dist/FiscalCalendar";
import { convertDateToUTC } from "@blisspointmedia/bpm-types/dist/formatUtils";
import { ValueLabelRecord } from "@blisspointmedia/bpm-types/src/App2Performance";
import {
  DateGroupingKey,
  DAY_DATE_LABEL,
  DAY_DATE_TOOLTIP_LABEL,
  MONTH_DATE_LABEL,
  MONTH_DATE_TOOLTIP_LABEL,
  QUARTER_DATE_LABEL,
  WEEK_DATE_LABEL,
  YEAR_DATE_LABEL
} from "../TVADCrossChannel/homePageConstants";
import { DateRange } from "../utils/types";

export const snapToMonday = (date: string): string =>
  Dfns.format(DATE_FORMAT, Dfns.startOfISOWeek(Dfns.parseISO(date)));

export const MILLISECONDS_IN_A_DAY = 1000 * 60 * 60 * 24;
export const SHORT_DATE_FORMAT = "MM/dd";
const DATE_FORMAT = "yyyy-MM-dd";
export const CURRENT_WEEK_START = R.pipe(Dfns.startOfISOWeek, Dfns.format(DATE_FORMAT))(new Date());

/**
 * Splits a given date range into an array of formatted date strings representing the start of each week within the range.
 * The range is inclusive of both the start and end dates.
 *
 * @param {DateRange} dateRange - The range of dates to be split into weeks.
 * @returns {string[]} An array of formatted date strings, each representing the start of a week within the date range.
 */
export const getWeeksFromRange = (dateRange: DateRange): string[] => {
  let weeks: string[] = [];
  let currentWeek = dateRange.start;
  while (currentWeek <= dateRange.end) {
    weeks.push(currentWeek);
    currentWeek = R.pipe(Dfns.parseISO, Dfns.addWeeks(1), Dfns.format(DATE_FORMAT))(currentWeek);
  }

  return weeks;
};

/**
 * Checks if the provided date is a valid Date object.
 *
 * @param {Date} date - The date to validate.
 * @returns {boolean} If it's a valid date or not
 */
export const validDate = (date: Date): boolean => {
  if (R.isNil(date)) {
    return false;
  }
  const timeNow = new Date(date);
  return !R.isNil(timeNow) && !R.isNil(timeNow.getDate()) && !isNaN(timeNow.getDate());
};

/**
 * Returns a formatted date string representing the date exactly N days prior to today.
 *
 * @param {number} [daysAgo=1] - The number of days ago to subtract from the current date (optional, defaults to 1).
 * @param {string} [format=DATE_FORMAT] - The format in which to return the date (optional, defaults to the predefined DATE_FORMAT).
 * @returns {string} The formatted date string of the specified days ago.
 */
export const getFormattedDaysAgo = (daysAgo = 1, format = DATE_FORMAT): string =>
  R.pipe(Dfns.subDays(daysAgo), Dfns.format(format))(new Date());

/**
 * Returns a formatted date string representing the date exactly N months prior to today.
 *
 * @param {number} [monthsAgo=1] - The number of months ago to subtract from the current date (optional, defaults to 1).
 * @param {string} [format=DATE_FORMAT] - The format in which to return the date (optional, defaults to the predefined DATE_FORMAT).
 * @returns {string} The formatted date string of the specified months ago.
 */
export const getFormattedMonthsAgo = (monthsAgo = 1, format = DATE_FORMAT): string =>
  R.pipe(Dfns.subMonths(monthsAgo), Dfns.format(format))(new Date());

export const formatDateLabel = (
  date: string,
  fiscalCal?: CompanyFiscalCal,
  groupingKey = "Day",
  isTooltip = false,
  customFormatMap: Partial<Record<DateGroupingKey, string>> | undefined = undefined
): string => {
  if (!date || !Dfns.isValid(Dfns.parseISO(date))) {
    return "";
  }
  const { calType, seedDate, generatedPatternCal } = fiscalCal || {};

  const parsedDate = Dfns.parseISO(date);

  let dateToUse = parsedDate;

  let format = "";
  const customFormat =
    customFormatMap && customFormatMap[groupingKey] ? customFormatMap[groupingKey] : null;
  switch (groupingKey) {
    case "Day":
      format = isTooltip ? DAY_DATE_TOOLTIP_LABEL : DAY_DATE_LABEL;
      break;
    case "Week":
      format = isTooltip ? DAY_DATE_TOOLTIP_LABEL : WEEK_DATE_LABEL;
      break;
    case "Month":
      switch (calType) {
        case FiscalCalType.PATTERN: {
          dateToUse = getMonthLabelPattern(date, generatedPatternCal as FiscalPatternCal);
          break;
        }
      }
      format = isTooltip ? MONTH_DATE_TOOLTIP_LABEL : MONTH_DATE_LABEL;
      break;
    case "Quarter": {
      switch (calType) {
        case FiscalCalType.YEARLY: {
          dateToUse = getQuarterLabelYearly(date, seedDate as string);
          break;
        }
        case FiscalCalType.PATTERN: {
          dateToUse = getQuarterLabelPattern(date, generatedPatternCal as FiscalPatternCal);
          break;
        }
      }
      format = QUARTER_DATE_LABEL;
      break;
    }
    case "Year": {
      switch (calType) {
        case FiscalCalType.YEARLY: {
          dateToUse = getYearLabelYearly(date, seedDate as string);
          break;
        }
        case FiscalCalType.PATTERN: {
          dateToUse = getYearLabelPattern(date, generatedPatternCal as FiscalPatternCal);
          break;
        }
      }
      format = YEAR_DATE_LABEL;
      break;
    }
    default:
      format = DAY_DATE_LABEL;
  }
  if (customFormat) {
    format = customFormat;
  }

  const formattedDate = Dfns.format(format, dateToUse);

  if (groupingKey === "Week") {
    return `w/o ${formattedDate}`;
  }

  return formattedDate;
};

export const dateToMddyy = (date: string): string => {
  if (!date || !Dfns.isValid(Dfns.parseISO(date))) {
    return "";
  }
  return Dfns.format("M/dd/yy", Dfns.parseISO(date));
};

export const parseEpochDate = (dateNum: number): BpmDateString => {
  const dateObj = new Date(dateNum);
  const utcDateObj = convertDateToUTC(dateObj);
  return Dfns.format("yyyy-MM-dd", utcDateObj) as BpmDateString;
};

export const dateToMddyyDashed = (date: string): string => {
  if (!date || !Dfns.isValid(Dfns.parseISO(date))) {
    return "";
  }
  return Dfns.format("M-dd-yy", Dfns.parseISO(date));
};

/**
 * Gets the current timezone abbreviation (e.g., "PST", "EST", "UTC")
 * @returns {string} The timezone abbreviation
 */
export const getTimeZoneAbbr = (): string => {
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  return (
    new Intl.DateTimeFormat("en", {
      timeZone,
      timeZoneName: "short",
    })
      .formatToParts(new Date())
      .find(part => part.type === "timeZoneName")?.value || ""
  );
};

/**
 * Generates time options in 12-hour format with 1 hour intervals
 * @returns {Array<ValueLabelRecord>} Array of time options
 */
export const generateTimeOptions = (): Array<ValueLabelRecord> => {
  const timeOptions: Array<ValueLabelRecord> = [];

  for (let hour = 6; hour <= 18; hour++) {
    const timeStr = `${hour % 12 || 12}:00${hour < 12 ? "am" : "pm"}`;
    const value = timeStr;
    const label = timeStr;
    timeOptions.push({ value, label });
  }

  return timeOptions;
};

/**
 * Converts user time string to UTC time string
 * @param {string} timeStr - Time string in format like "2:30pm" or "10:15am"
 * @returns {string} UTC time string in format "HH:mm:ss"
 */
export const convertUserTimeToUTC = (timeStr: string): string => {
  const userTimezone = moment.tz.guess();

  const time = timeStr.toLowerCase();
  let [hours, minutes] = time.replace(/[amp]/g, "").split(":");
  let hour = parseInt(hours);

  if (time.includes("pm") && hour !== 12) {
    hour += 12;
  }
  if (time.includes("am") && hour === 12) {
    hour = 0;
  }

  const userTime = moment.tz(
    [moment().year(), moment().month(), moment().date(), hour, parseInt(minutes)],
    userTimezone
  );

  const utcTime = userTime.utc();

  return utcTime.format("HH:mm:ss");
};

/**
 * Converts UTC time string to user time string
 * @param {string} utcTimeStr - UTC time string in format "HH:mm:ss" or "HH:mm"
 * @returns {string} User time string in format like "2:30pm" or "10:15am"
 */
export const convertUTCToUserTime = (utcTimeStr: string): string => {
  const userTimezone = moment.tz.guess();

  const [hours, minutes] = utcTimeStr.split(":");
  const hour = parseInt(hours);
  const minute = parseInt(minutes);

  const utcTime = moment.utc([moment().year(), moment().month(), moment().date(), hour, minute]);

  const userTime = utcTime.tz(userTimezone);

  return userTime.format("h:mma").toLowerCase();
};
